import React, { useState, useEffect, useRef } from 'react';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { Pagination, Skeleton, Stack, Tooltip, Chip, TextField, Menu } from '@mui/material';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import { useReduxLeads } from '@Hooks/reduxLeadsHook/useReduxLeads';
import DOMPurify from 'dompurify';
import {
    X, CheckCircle, AlertCircle, Clock, TrendingUp, Send, Clock3,
    Trash2, RefreshCw, Users, Calendar as CalendarIcon, Phone, MessageSquare,
    Hash, Activity, Info, ChevronRight, Zap, Download, ArrowLeft, CirclePause, Search, PenLine
} from 'lucide-react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import fileDownload from 'js-file-download';
import html2canvas from 'html2canvas';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import useCountdown from '@Hooks/useCountdown';
import useParamHook from '@Hooks/useParamHook';
import useDebounce from '@Hooks/useDebounce';
import ContactDetailsPage from '@CommonFunctionalDisplay/ContactDetailsPage';
import ExportDownloadForm from '@FormForms/ExportDownloadForm';
import FailedMsgView from '@ComponentViews/FailedMsgView';
import UrlEncryptionService from '@Helpers/UrlEncryptionService';
import WhatsappTemplatePreview from '@CommonRepresentationalDisplay/WhatsappTemplatePreview';
import Calendar from '@CommonRepresentationalDisplay/Calendar';
import CampaignRecipientsView from '@ComponentViews/CampaignRecipientsView';
import CampaignJobViewService from '@Helpers/URLHelpers/WeboutService/Views/CampaignJobViewService';
import CampaignRunService from '@Helpers/URLHelpers/WeboutService/CampaignRunService';
import NameCardService from '@Helpers/URLHelpers/WeboutService/NameCardService';
import CampaignStatusViewService from '@Helpers/URLHelpers/CampaignStatusViewService';
import ErrorHandler from '@Root/ErrorHandler';
import './WhatsappCampaignStatusPage.scss';

const DEFAULT_DOWNLOAD_OPTIONS = [
    { id: 0, field: 'campaignName', description: 'Title of the Campaign.', display: 'Campaign Name', customField: false },
    { id: 1, field: 'templateName', description: 'Template Name for campaign.', display: 'Template Name', customField: false },
    { id: 2, field: 'campaignRunStatus', description: 'Status of the Campaign.', display: 'Campaign Status', customField: false },
    { id: 3, field: 'campaignScheduleTime', description: 'Campaign Run At.', display: 'Campaign Run At', customField: false },
    { id: 4, field: 'name', description: 'Full name of the user.', display: 'User name', customField: false },
    { id: 5, field: 'email', description: 'Email address of the user.', display: 'Email', customField: false },
    { id: 6, field: 'contactNumber', description: 'Phone number of the user.', display: 'Phone Number', customField: false },
    { id: 7, field: 'department', description: 'Department of the user.', display: 'Department', customField: false },
    { id: 9, field: 'campaignStatus', description: 'Message delivery status.', display: 'Msg Status', customField: false },
    { id: 10, field: 'msgError', description: 'Reason for campaign/message failure.', display: 'Failed Reasoning', customField: false },
    { id: 11, field: 'retryCount', description: 'Number of retry attempts.', display: 'Retry Count', customField: false },
    { id: 12, field: 'nextRetryTimeStamp', description: 'Next retry scheduled time.', display: 'Next Retry At', customField: false },
    { id: 13, field: 'labels', description: 'Attached label in contacts.', display: 'Labels', customField: false },
    { id: 8, field: 'organization', description: 'Organization of the user.', display: 'Organization', customField: false },
    { id: 14, field: 'creatorUserFullName', description: 'Campaign Run By', display: 'Campaign Run By', customField: false }
];

function WhatsappCampaignStatusPage() {

    const campaignId = useParamHook('campaignId');
    const navigate = useNavigate();
    const [cursor, setCursor] = useState('auto');
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState('all');
    const [campaign, setCampaign] = useState({});
    const [stats, setStats] = useState({});
    const [campaignList, setCampaignList] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 500, true);
    const [activeTab, setActiveTab] = useState('logs');
    const [selectedContact, setSelectedContact] = useState(null);
    const [showCalender, setShowCalender] = useState(false);
    const [anchorCalenderMenu, setAnchorCalenderMenu] = useState(false);
    const [highlightedDates, setHighlightedDates] = useState([]);
    const [contactDetails, setContactDetails] = useState({});
    const [showContact, setShowContact] = useState(false);
    const [contactModalOpen, setContactModalOpen] = useState(false);
    const [editCampaignName, setEditCampaignName] = useState(false);
    const [campaignName, setCampaignName] = useState('');
    const size = 10;
    const [campaignJobFilter, setCampaignJobFilter] = useState({
        campaignJobViewFilter: {
            searchQuery: '', campaignRunType: '', campaignRunIdList: [], campaignStatusList: []
        },
        page: 0, size
    });
    const [showRecipients, setShowRecipients] = useState(false);
    const [showConfirmDownloadForm, setShowConfirmDownloadForm] = useState(false);
    const { currentUserProjectPermissions } = useReduxProject();
    const { numbersList } = useReduxLeads();
    const campaignJobViewService = new CampaignJobViewService();
    const campaignStatusViewService = new CampaignStatusViewService();
    const nameCardService = new NameCardService();
    const campaignRunService = new CampaignRunService();
    const urlEncryptionService = new UrlEncryptionService();
    const errorHandler = new ErrorHandler();
    const campaignListRef = useRef(null);
    const dataCache = useRef({});
    const contactCache = useRef({});
    const dateFormat = 'DD MMM YYYY';
    const className = 'whatsapp-campaign-status';

    useEffect(() => { renderData(); }, []);
    useEffect(() => { if (Object.keys(campaign).length) fetchLabels(page); }, [page, campaign]);

    useEffect(() => {
        handleSearch();
    }, [debouncedSearch]);

    const renderData = async () => { await fetchCampaignRun(); await fetchStats(); };

    const fetchCampaignRun = async () => {
        setLoading(true);
        try {
            let res = await campaignRunService.getById(campaignId);
            if (res.status < 400) {
                res = JSON.parse(res.body);
                setCampaignName(res.name);
                setCampaign(res);
                handleHighlightedDates(res);
            }
        } catch (err) { errorHandler.handleError(err); }
        setLoading(false);
    };

    const fetchLabels = async (pg) => {
        setLoading(true);
        try {
            const apiPage = pg - 1;
            const key = `page-${apiPage}`;
            if (dataCache.current[key]) { setCampaignList(dataCache.current[key]); setLoading(false); return; }
            const filter = { ...campaignJobFilter };
            filter.campaignJobViewFilter.searchQuery = search;
            filter.campaignJobViewFilter.campaignRunType = campaign.itemType;
            filter.campaignJobViewFilter.campaignRunIdList = [campaign?.id];
            filter.page = apiPage;
            filter.size = size;
            await fetchData(filter, key);
        } catch (err) { errorHandler.handleError(err); }
        setLoading(false);
    };

    const handleSearch = async()=> {
        const apiPage = page - 1;
        const filter = { ...campaignJobFilter };
        filter.campaignJobViewFilter.searchQuery = debouncedSearch;
        filter.campaignJobViewFilter.campaignRunType = campaign.itemType;
        filter.campaignJobViewFilter.campaignRunIdList = [campaign?.id];
        filter.page = apiPage;
        filter.size = size;
        await fetchData(filter, '');
    }

    const fetchData = async (filter, cacheKey) => {
        try {
            let res = await campaignJobViewService.getListFiltered(filter);
            if (res.status < 400) {
                res = JSON.parse(res.body);
                if (cacheKey) dataCache.current[cacheKey] = res.campaignJobViewList;
                setCampaignList(res.campaignJobViewList);
                setTotalPages(res.totalPages);
            }
        } catch (err) { errorHandler.handleError(err); }
        setLoading(false);
    };

    const fetchStats = async () => {
        try {
            let res = await campaignStatusViewService.getByCampaignRunId(campaignId);
            if (res.status < 400) setStats(JSON.parse(res.body));
        } catch (err) { errorHandler.handleError(err); }
    };

    const retryMap = {
        completedOneRetry: 1,
        completedTwoRetry: 2,
        completedThreeRetry: 3,
        completedFourRetry: 4,
        completedFiveRetry: 5,
        completedSixRetry: 6,
    };

    const getRetryCount = (status) => retryMap[status] || null;

    const handleStatusChange = (s) => {
        const f = { ...campaignJobFilter };
        if(s === 'failedRetry') {
            f.campaignJobViewFilter.retryCountList = [1, 2, 3, 4, 5 ,6];
            f.campaignJobViewFilter.statusList = ['failed'];
        } else if (s === 'delivered') {
            f.campaignJobViewFilter.campaignStatusList = ['read', 'delivered', 'sent'];
            f.campaignJobViewFilter.retryCountList = [];
        } else {
            if (retryMap[s]) {
                f.campaignJobViewFilter.retryCountList = [retryMap[s]];
                f.campaignJobViewFilter.campaignStatusList = [];
            } else {
                f.campaignJobViewFilter.retryCountList = [];
                f.campaignJobViewFilter.campaignStatusList = s === 'all' ? [] : [s];
            }
        }
        setCampaignJobFilter(f);
        setFilterStatus(s);
        setPage(1);
        fetchData(f, '');
    };

    const closeContactModal = () => {
        setContactModalOpen(false);
        setSelectedContact(null);
    };

    const handleSelectContact = async (contact) => {
        setSelectedContact(contact);
        setContactModalOpen(true);
        if (!contact?.nameCardId) return;
        const cacheKey = contact.nameCardId;
        if (!!contactCache.current[cacheKey]) {
            setContactDetails(contactCache.current[cacheKey]);
            return;
        }
        try {
            let response = await nameCardService.getById(cacheKey);
            response = JSON.parse(response.body);
            const finalResponse = {
                ...response,
                ...contact,
                ...(JSON.parse(contact.campaignJobDetails || '{}'))
            };
            contactCache.current[cacheKey] = finalResponse;
            setContactDetails(finalResponse);
        } catch {
            setContactDetails({});
        }
    };

    const handleStopRetries = async ()=> {
        try {
            let body = {
                campaignRunId: campaign.id,
            }
            let response = await campaignRunService.stopRetries(body);
            if(response.status < 400) {
                await renderData();
            }
        } catch(err) {
            errorHandler.handleError(err);
        }
    }

    const handleHighlightedDates = (campaign)=> {
        let details = JSON.parse(campaign?.details);
        const createTimeStamp = campaign?.scheduleTimeStamp || campaign?.createTimeStamp;
        let today = new Date(createTimeStamp + (1000 * 60 * 60 * 24));
        let occurrences = !!details?.retryCount ? details?.retryCount : 3;
        let repeat = 1;
        let dates = [];
        for (let i = 0; i < occurrences; i++) {
            dates.push(moment(today).add(i * repeat, 'days').format(dateFormat));
        }
        dates = [...new Set(dates)].sort((a, b) => new Date(a) - new Date(b));
        setHighlightedDates(dates);
    }

    const getStatusConfig = (status) => {
        switch (status) {
            case 'sent': return { icon: <Send size={13} />, cls: 'badge--sent', label: 'Sent' };
            case 'delivered': return { icon: <CheckCircle size={13} />, cls: 'badge--delivered', label: 'Delivered' };
            case 'read': return { icon: <TrendingUp size={13} />, cls: 'badge--opened', label: 'Read' };
            case 'retry': return { icon: <RefreshCw size={13} />, cls: 'badge--retry', label: 'Retry' };
            case 'failed':
            case 'error': return { icon: <AlertCircle size={13} />, cls: 'badge--failed', label: 'Failed' };
            case 'paused' : return { icon: <AlertCircle size={13} />, cls: 'badge--failed', label: 'Paused' };
            case 'unsubscribed' : return { icon: <AlertCircle size={13} />, cls: 'badge--failed', label: 'Unsubscribed' };
            case 'queued':
            case 'created' : return { icon: <Clock3 size={13} />, cls: 'badge--queued', label: 'In Queue' };
            default: return { icon: <Clock size={13} />, cls: 'badge--pending', label: 'Pending' };
        }
    };

    const getCampaignStatusConfig = (status) => {
        const map = {
            completed: { label: 'Completed', cls: 'run-badge--completed' },
            running: { label: 'Running', cls: 'run-badge--running' },
            scheduled: { label: 'Scheduled', cls: 'run-badge--scheduled' },
            deleted: { label: 'Deleted', cls: 'run-badge--deleted' },
            paused: { label: 'Paused', cls: 'run-badge--paused' },
            failed: { label: 'Failed', cls: 'run-badge--failed' },
            unsubscribed: { label: 'Unsubscribed', cls: 'run-badge--failed' },
            queued: { label: 'Queued', cls: 'run-badge--queued' },
        };
        return map[status] || { label: status, cls: 'run-badge--default' };
    };

    const fmt = (ts) => ts
        ? new Intl.DateTimeFormat('en-US', {
            year: 'numeric', month: 'short', day: '2-digit',
            hour: '2-digit', minute: '2-digit'
        }).format(new Date(ts))
        : '—';

    const avatarStyle = (name = '') => {
        const p = [
            { bg: '#DBEAFE', color: '#1D4ED8' },
            { bg: '#DCFCE7', color: '#15803D' },
            { bg: '#FEF9C3', color: '#A16207' },
            { bg: '#EDE9FE', color: '#6D28D9' },
            { bg: '#FCE7F3', color: '#BE185D' },
            { bg: '#FFEDD5', color: '#C2410C' },
        ];
        return p[(name.charCodeAt(0) || 0) % p.length];
    };

    const { timeLeft, formatTimer } = useCountdown(stats.nextRetryTimeStamp);
    const total = stats.totalCount ?? 0;
    const sent = stats.sentCount ?? 0;
    const delivered = stats.deliveredCount ?? 0;
    const pending = stats.pendingCount ?? 0;
    const queued = stats.queuedCount ?? 0;
    const totalQueue = pending + queued;
    const opened = stats.readCount ?? 0;
    const failed = stats.failedCount ?? 0;
    const unsubscribed = stats.unsubscribedCount ?? 0;
    const retry = stats.retryCount ?? 0;
    const completedRetry = stats.completedRetry ?? 0;
    const failedRetry = stats.failedRetry ?? 0;
    const completedOneRetry = stats.completedOneRetry ?? 0;
    const completedTwoRetry = stats.completedTwoRetry ?? 0;
    const completedThreeRetry = stats.completedThreeRetry ?? 0;
    const completedFourRetry = stats.completedFourRetry ?? 0;
    const completedFiveRetry = stats.completedFiveRetry ?? 0;
    const completedSixRetry = stats.completedSixRetry ?? 0;
    const deliveryRate = total > 0 ? Math.round(((delivered + opened + sent) / total) * 100) : 0;
    const openRate = (delivered + opened) > 0 ? Math.round((opened / (delivered + opened)) * 100) : 0;
    const failedRate = total > 0 ? Math.round((failed / total) * 100) : 0;
    const retryRate = total > 0 ? Math.round((retry / total) * 100) : 0;

    const campaignDetails = (() => { try { return JSON.parse(campaign.details || '{}'); } catch { return {}; } })();
    const campaignHistory = (() => { try { return JSON.parse(campaign.history || '[]'); } catch { return []; } })();
    const sendToCount = (() => { try { return JSON.parse(campaign.sendTo || '[]').length; } catch { return 0; } })();

    const statusFilters = [
        { key: 'all', label: 'All', count: total, icon: <Send size={13} /> },
        { key: 'read', label: 'Read', count: opened, icon: <TrendingUp size={13} />  },
        { key: 'failed', label: 'Failed', count: failed, icon: <AlertCircle size={13} /> },
        ...(retry > 0 ? [{ key: 'retry', label: 'Retry', count: retry, icon: <RefreshCw size={13} /> }] : []),
        { key: 'delivered', label: 'Delivered', count: (delivered + opened + sent), icon: <CheckCircle size={13} />},
        ...(completedOneRetry > 0 ? [{ key: 'completedOneRetry', label: 'Delivered #1', count: completedOneRetry, icon: <CheckCircle size={13} /> }]: []),
        ...(completedTwoRetry > 0 ? [{ key: 'completedTwoRetry', label: 'Delivered #2', count: completedTwoRetry, icon: <CheckCircle size={13} /> }] : []),
        ...(completedThreeRetry > 0 ? [{ key: 'completedThreeRetry', label: 'Delivered #3', count: completedThreeRetry, icon: <CheckCircle size={13} /> }] : []),
        ...(completedFourRetry > 0 ? [{ key: 'completedFourRetry', label: 'Delivered #4', count: completedFourRetry, icon: <CheckCircle size={13} /> }] : []),
        ...(completedFiveRetry > 0 ? [{ key: 'completedFiveRetry', label: 'Delivered #5', count: completedFiveRetry, icon: <CheckCircle size={13} /> }] : []),
        ...(completedSixRetry > 0 ? [{ key: 'completedSixRetry', label: 'Delivered #6', count: completedSixRetry, icon: <CheckCircle size={13} /> }] : []),
        ...(totalQueue > 0 ? [{ key: 'queued', label: 'Queued', count: totalQueue, icon: <Clock3 size={13} />, }] : [])
    ];

    const filteredData = campaignList.filter(i => {
        if (filterStatus === 'all') {
            return true;
        }
        if(filterStatus === 'delivered') {
            return ['read', 'delivered', 'sent'].includes(i.campaignStatus);
        }
        if (filterStatus === 'failedRetry') {
            return (
                i.campaignStatus === 'failed' &&
                [1, 2, 3, 4, 5, 6].includes(i.retryCount)
            );
        }
        const retryCount = getRetryCount(filterStatus);
        if (retryCount) {
            return (
                !['retry', 'failed'].includes(i.campaignStatus) &&
                i.retryCount === retryCount
            );
        }
        return i.campaignStatus === filterStatus;
    });

    const runBadge = getCampaignStatusConfig(campaign.status);

    const RowSkeleton = () => (
        <Stack spacing={1.5} sx={{p: 1}}>
            {[...Array(3)].map((_, i) => (
                <Skeleton key={i} variant="rounded" height={56} sx={{ borderRadius: '8px' }} />
            ))}
        </Stack>
    );

    if (Object.keys(campaign).length === 0) return (
        <div className="whatsapp-campaign-status">
            <div className="whatsapp-campaign-status__header" style={{padding: '10px'}}>
                <div>
                    <Skeleton variant="text" width={240} height={34} />
                    <Skeleton variant="text" width={300} height={18} sx={{ mt: 0.5 }} />
                </div>
                <Skeleton variant="circular" width={34} height={34} />
            </div>
            <div className="whatsapp-campaign-status__divider" />
            <div className="whatsapp-campaign-status__content" style={{padding: '10px'}}>
                <Skeleton variant="rounded" height={130} sx={{ borderRadius: '12px' }} />
                <div style={{ display: 'flex', gap: 12, padding: '10px' }}>
                    {[...Array(4)].map((_, i) => (
                        <Skeleton key={i} variant="rounded" height={90} sx={{ flex: 1, borderRadius: '12px' }} />
                    ))}
                </div>
                <Skeleton variant="rounded" height={300} sx={{ borderRadius: '12px' }} />
            </div>
        </div>
    );

    const toggleSubscribe = async ()=> {
        setCursor('wait');
        try {
            const cacheKey = selectedContact?.nameCardId;
            let body = {
                id: cacheKey
            };
            let response = await nameCardService.toggleSubscribe(body);
            if(response.status < 400) {
                response = JSON.parse(response.body);
                let finalResponse = {
                    ...selectedContact,
                    ...contactDetails,
                    isSubscribed: response.isSubscribed
                };
                setContactDetails((prev)=> ({...finalResponse}));
                contactCache.current[cacheKey] = finalResponse;
            }
        } catch(err) {
            errorHandler.handleError(err);
        }
        setCursor('auto');
    }

    const dataURLtoFile = (dataurl) => {
        var arr = dataurl?.split(','),
            mime = arr[0]?.match(/:(.*?);/)[1],
            bstr = atob(arr[1]),
            n = bstr.length,
            u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new File([u8arr], { type: mime });
    };

    const updateCampaignName = async()=> {
        try {
            let bodyObj = {
                id: campaign?.id,
                name: campaignName
            };
            let res = await campaignRunService.updateCampaignRunName(bodyObj);
            if (res.status < 400) {
                res = JSON.parse(res.body);
                setCampaign(res);
            }
        } catch(err) {
            errorHandler.handleError(err);
        }
    }

    const editTitleOnKeyPress = (e) => {
        if(e.keyCode === 13) {
            toggleEditCampaignName()
        }
    }

    const toggleEditCampaignName = () => {
        if(editCampaignName) {
            updateCampaignName();
        }
        setEditCampaignName(!editCampaignName);
    }

    const handleNameChange = (e) => {
        let sanitizedTitle = DOMPurify.sanitize(e.target.value);
        setCampaignName(sanitizedTitle);
    }

    const toggleConfirmDownloadForm = ()=> {
        setShowConfirmDownloadForm(!showConfirmDownloadForm);
    }

    const handleExport = async ({selectedOptions, splitBy, exportType }) => {
        let bodyObj = {
              exportType,
              campaignRunId: campaign?.id,
              splitBy,
              filterRequest: {
                  campaignJobViewFilter: {
                      campaignRunIdList: [campaign.id],
                      campaignRunType: 'whatsapp',
                      campaignStatusList: splitBy === 'error' ? ['failed', 'error', 'retry'] : [],
                  },
                  options: selectedOptions.map(o => JSON.stringify(o)),
              }
        }
        return await campaignJobViewService.exportReport(bodyObj);
    };

    const handleCloseContactView = ()=> {
        setShowContact(false);
    }

    const handleOpenContactView = (selectedChat)=> {
        setSelectedContact((prev)=> selectedChat);
        setShowContact(true);
    }

    const handleCalenderMenu = (event) => {
        event.stopPropagation();
        if(!showCalender){
            if(!!event) {
                setAnchorCalenderMenu(event.currentTarget);
                setShowCalender(true);
            }
        } else {
            setAnchorCalenderMenu(null);
            setShowCalender(false);
        }
    }

    const getError = (item)=> {
        const details = item.campaignJobDetails ? JSON.parse(item.campaignJobDetails) : {};
        const sc = getStatusConfig(item.campaignStatus);
        const showTip = sc.label === 'Failed' || sc.label === 'Retry' || sc.label === 'Unsubscribed';
        return (
            showTip ?
                <div className="whatsapp-campaign-status__col-error ">
                    <Tooltip title={details?.error ?? 'Error'} placement="top" arrow>
                        {details?.error ?? 'Error'}
                    </Tooltip>
                </div>
            :
                <div className="whatsapp-campaign-status__col-error "/>
        )
    }

    const getItemName = (item) => {
        const av = avatarStyle(item.name);
        return (
            <div className="whatsapp-campaign-status__col-recipient whatsapp-campaign-status__recipient">
                <button
                    type="button"
                    className="whatsapp-campaign-status__contact-link"
                    onClick={() => handleOpenContactView(item)}
                >
                    <div
                        className="whatsapp-campaign-status__avatar"
                        style={{ background: av.bg, color: av.color }}
                    >
                        {(item.name || '?').charAt(0).toUpperCase()}
                    </div>
                    <div className="whatsapp-campaign-status__recipient-info">
                        <span className="whatsapp-campaign-status__recipient-name">{item.name}</span>
                        <span className="whatsapp-campaign-status__recipient-phone">{item.contactNumber}</span>
                    </div>
                </button>
            </div>
        )
    }

    const getCreateDate = (row)=> {
        let lead = row;
        return (
            <div className="whatsapp-campaign-status__col-time">
               {fmt((lead.nextRetryTimeStamp === 0 ? lead.createTimeStamp : lead.nextRetryTimeStamp))}
            </div>
        );
    }

    const getItemStatus = (row) => {
        const item = row;
        const details = item.campaignJobDetails ? JSON.parse(item.campaignJobDetails) : {};
        const sc = getStatusConfig(item.campaignStatus);
        const showTip = sc.label === 'Failed' || sc.label === 'Retry' || sc.label === 'Unsubscribed';
        const label = sc.label === 'Retry' ? (timeLeft.completed ? 'Running' : sc.label) : sc.label;
        const retryCount = sc.label === 'Retry' ? item.retryCount : 0;
        return (
            <div className="whatsapp-campaign-status__col-status">
                {showTip ? (
                    <Tooltip title={details?.error ?? 'Error'} placement="top" arrow>
                        <span className={`whatsapp-campaign-status__badge ${sc.cls}`}>
                            {sc.icon}{label}
                            {retryCount > 0 &&
                                <span className="whatsapp-campaign-status__retry-count">#{retryCount}</span>}
                        </span>
                    </Tooltip>
                ) : (
                    <span className={`whatsapp-campaign-status__badge ${sc.cls}`}>
                        {sc.icon}{sc.label}
                    </span>
                )}
            </div>
        );
    };

    const getFooter = ()=> {
        return (
            totalPages > 1 &&
                <div className="whatsapp-campaign-status__pagination">
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(_, v) => setPage(v)}
                        shape="rounded"
                        size="small"
                    />
                </div>
        )
    }

    const emptyList = ()=> {
        return (
            <div className="empty-leads">
               <h4> No deliveries found for this filter.</h4>
           </div>
        )
    }

    const gridColumn = 3 + (retry > 0 ? + 1 : 0) + (totalQueue > 0 ? + 1 : 0);
    const statsGridColumn = gridColumn > 3 ? gridColumn : 4;

    const getPhoneNumber = ()=> {
        let phoneNumber = numbersList.find((item)=> item.id === campaignDetails.phoneNumberId);
        return phoneNumber?.display_phone_number ?? '';
    }

    return (
        <>
            <div
                className={className}
                id="divToExport"
                style={{ cursor }}
            >
                <div className="whatsapp-campaign-status__header">
                    <div className="whatsapp-campaign-status__header-left">
                        <div className="whatsapp-campaign-status__title-row">
                            {editCampaignName ? (
                                <div className={className + '__mobileTitle'}>
                                    <div className={className + '__mobileTitle__edit'}>
                                        <TextField
                                            fullWidth
                                            placeholder='Enter title...'
                                            value={campaignName}
                                            onChange={handleNameChange}
                                            onKeyDown={editTitleOnKeyPress}
                                            onBlur={toggleEditCampaignName}
                                            inputProps={{ maxLength: 200 }}
                                            disabled={!currentUserProjectPermissions?.allowCreate || cursor === 'wait'}
                                            autoFocus
                                        />
                                    </div>
                                </div>
                            ) : (
                                <h2 className="whatsapp-campaign-status__title" >{campaignName}</h2>
                            )}
                            <span className={`whatsapp-campaign-status__run-badge ${runBadge.cls}`}>
                                {campaign.status === 'deleted' && <Trash2 size={11} />}
                                {runBadge.label}
                            </span>
                        </div>
                        <p className="whatsapp-campaign-status__subtitle">
                            <p className="wcs-hero__sub">Campaign Run On {fmt(campaign.scheduleTimeStamp)}</p>
                        </p>
                    </div>
                    <div className='flex-row-center-0p5rem-gap'>
                        <span className="whatsapp-campaign-status-badge">
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" /><path d="M12 0C5.373 0 0 5.373 0 12c0 2.134.559 4.133 1.535 5.873L0 24l6.294-1.504A11.945 11.945 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.807 9.807 0 01-5.032-1.386l-.361-.214-3.735.893.924-3.64-.235-.373A9.785 9.785 0 012.182 12C2.182 6.58 6.58 2.182 12 2.182c5.42 0 9.818 4.398 9.818 9.818 0 5.42-4.398 9.818-9.818 9.818z" />
                            </svg>
                            WhatsApp
                        </span>
                        <div className="whatsapp-campaign-status-actions">
                            <Tooltip title="Export" arrow>
                                <button className="whatsapp-campaign-status-action-btn" onClick={toggleConfirmDownloadForm}>
                                    <Download size={14} />
                                    Export
                                </button>
                            </Tooltip>
                        </div>
                        <div className="whatsapp-campaign-status-actions">
                            <Tooltip title="Refresh" arrow>
                                <button className="whatsapp-campaign-status-action-btn" onClick={renderData}>
                                    <RefreshCw size={14} />
                                    Refresh
                                </button>
                            </Tooltip>
                        </div>
                        {
                            (campaign?.status === 'scheduled' || campaign?.status === 'paused') &&
                                <div className="whatsapp-campaign-status-actions">
                                    <Tooltip title="Update" arrow>
                                        <button className="whatsapp-campaign-status-action-btn" onClick={()=> navigate('update')}>
                                            <PenLine size={14} />
                                            Update
                                        </button>
                                    </Tooltip>
                                </div>
                        }
                        <button
                            className="whatsapp-campaign-status__close"
                            onClick={() => navigate(-1)}
                            aria-label="Close"
                        >
                            <X size={20} />
                        </button>
                    </div>
                </div>
                <section className={`${className}__stats`}
                    style={{
                        gridTemplateColumns: `repeat(${statsGridColumn}, minmax(0, 1fr))`,
                    }}>
                    <div className={`${className}__stat-card`}>
                        <div
                            className={`${className}__stat-icon ${className}__stat-icon--blue`}
                        >
                            <Send size={18} />
                        </div>

                        <div>
                            <span className={`${className}__stat-label`}>
                                Total sent
                            </span>
                            <strong className={`${className}__stat-value`}>
                                {sent + delivered + opened + failed + retry + queued}
                            </strong>
                        </div>
                    </div>

                    <div className={`${className}__stat-card`}>
                        <div
                            className={`${className}__stat-icon ${className}__stat-icon--green`}
                        >
                            <TrendingUp size={18} />
                        </div>

                        <div>
                            <span className={`${className}__stat-label`}>
                                Read/Delivered
                            </span>
                            <strong className={`${className}__stat-value`}>
                                {`${opened}/${delivered + opened + sent}`}
                            </strong>
                        </div>

                        <span className={`${className}__stat-percent ${className}__stat-percent--green`}>
                            {deliveryRate}%
                        </span>
                    </div>

                    <div className={`${className}__stat-card`}>
                        <div
                            className={`${className}__stat-icon ${className}__stat-icon--red`}
                        >
                            <AlertCircle size={18} />
                        </div>

                        <div>
                            <span className={`${className}__stat-label`}>
                                Failed
                            </span>
                            <strong className={`${className}__stat-value`}>
                                {failed}
                            </strong>
                        </div>
                        <span className={`${className}__stat-percent ${className}__stat-percent--red`}>
                            {failedRate}%
                        </span>
                    </div>

                    {retry > 0 && (
                        <div className={`${className}__stat-card`}>
                            <div
                                className={`${className}__stat-icon ${className}__stat-icon--orange`}
                            >
                                <RefreshCw size={18} />
                            </div>

                            <div>
                                <span className={`${className}__stat-label`}>
                                    In retry
                                </span>
                                <strong className={`${className}__stat-value`}>
                                    {retry}
                                </strong>
                            </div>
                            <span className={`${className}__stat-percent ${className}__stat-percent--orange`}>
                                {retryRate}%
                            </span>
                        </div>
                    )}
                    {totalQueue > 0 && (
                        <div className={`${className}__stat-card`}>
                            <div
                                className={`${className}__stat-icon ${className}__stat-icon--queued`}
                            >
                                <Clock3 size={18} />
                            </div>

                            <div>
                                <span className={`${className}__stat-label`}>
                                    In Queue
                                </span>
                                <strong className={`${className}__stat-value`}>
                                    {totalQueue}
                                </strong>
                            </div>
                        </div>
                    )}
                </section>

                <main className={`${className}__workspace`}>
                    <section className={`${className}__panel ${className}__panel--main`}>
                        {/* Tabs */}
                        <div className={`${className}__tabs`}>
                            <button
                                className={`${className}__tab ${
                                    activeTab === 'logs'
                                        ? `${className}__tab--active`
                                        : ''
                                }`}
                                onClick={() => setActiveTab('logs')}
                            >
                                <Activity size={15} />
                                Delivery logs
                            </button>

                            <button
                                className={`${className}__tab ${
                                    activeTab === 'details'
                                        ? `${className}__tab--active`
                                        : ''
                                }`}
                                onClick={() => setActiveTab('details')}
                            >
                                <Info size={15} />
                                Campaign details
                            </button>

                            <button
                                className={`${className}__tab ${
                                    activeTab === 'retryDetails'
                                        ? `${className}__tab--active`
                                        : ''
                                }`}
                                onClick={() => setActiveTab('retryDetails')}
                            >
                                <RefreshCw size={15} />
                                Retry details
                                {retry > 0 && (
                                    <span className={`${className}__tab-count`}>
                                        {retry}
                                    </span>
                                )}
                            </button>
                        </div>
                        {activeTab === 'logs' && (
                            <div className={`${className}__logs`}>
                                <div className={`${className}__section-heading ${className}__logs__heading`}>
                                    <div className='flex-row-center-0p5vw-gap'>
                                        <div className={`${className}__logs__heading__icon`}>
                                            <Activity size={18} />
                                        </div>
                                        <div>
                                            <h3>Message delivery</h3>
                                            <p>Track the status of every campaign recipient.</p>
                                        </div>
                                    </div>

                                    {retry > 0 && (
                                        <div className={`${className}__retry-actions`}>
                                            <div
                                                className={`${className}__retry-timer`}
                                            >
                                                <RefreshCw
                                                    size={14}
                                                    className={`${className}__retry-spin`}
                                                />
                                                Next retry
                                                <strong>
                                                    {formatTimer()}
                                                </strong>
                                            </div>

                                            <button
                                                className={`${className}__secondary-btn ${className}__secondary-btn--danger`}
                                                onClick={handleStopRetries}
                                            >
                                                <CirclePause size={15} />
                                                Stop retries
                                            </button>

                                            <button
                                                className={`${className}__secondary-btn`}
                                                onClick={handleCalenderMenu}
                                            >
                                                <CalendarIcon size={15} />
                                                Calendar
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* Search */}
                                <div className={`${className}__toolbar`}>
                                    <div className={`${className}__search`}>
                                        <Search size={16} />
                                        <input
                                            value={search}
                                            onChange={(e) =>
                                                setSearch(e.target.value)
                                            }
                                            placeholder="Search recipient or phone number..."
                                        />

                                        {search && (
                                            <button
                                                onClick={() => setSearch('')}
                                                className={`${className}__search-clear`}
                                            >
                                                <X size={14} />
                                            </button>
                                        )}
                                    </div>
                                </div>

                                <div className={`${className}__filters`}>
                                    <div className={`${className}__filters__title`}>
                                        <span>FILTER BY</span>
                                    </div>
                                    {statusFilters.map((filter) => (
                                        <button
                                            key={filter.key}
                                            className={`${className}__filter ${className}__filter__${filter.key}
                                            ${filterStatus === filter.key ? `${className}__filter__${filter.key}__active` : ''}
                                            ${filter.key.startsWith('completed') ? `${className}__filter__delivered` : ''}`}
                                            onClick={() => handleStatusChange(filter.key)}>
                                            {filter.icon}
                                            <span>{filter.label}</span>
                                            <b>{filter.count}</b>
                                        </button>
                                    ))}
                                </div>

                                {/* Table */}
                                <div className={`${className}__table-container`}>
                                    <DataTable
                                        value={campaignList}
                                        ref={campaignListRef}
                                        scrollable
                                        scrollHeight="flex"
                                        style={{ height: '100%' }}
                                        emptyMessage={emptyList}
                                        footer={getFooter}
                                        dataKey="rn"
                                        rowHover
                                    >
                                        <Column
                                            field="recipient"
                                            header="Recipient"
                                            body={getItemName}
                                        />

                                        <Column
                                            field="error"
                                            header="Error"
                                            body={getError}
                                        />

                                        <Column
                                            field="status"
                                            header="Status"
                                            body={getItemStatus}
                                        />

                                        <Column
                                            field="createDate"
                                            header="Sent at"
                                            body={getCreateDate}
                                        />
                                    </DataTable>
                                </div>
                            </div>
                        )}
                        {activeTab === 'details' && (
                            <div className={`${className}__details`}>
                                <div className={`${className}__section-heading`}>
                                    <div className='flex-row-center-0p5vw-gap'>
                                        <div className={`${className}__section-heading__icon`}>
                                            <Info size={18} />
                                        </div>
                                        <div>
                                            <h3>Campaign details</h3>
                                            <p>
                                                Configuration and performance
                                                information.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className={`${className}__detail-grid`}>
                                    {campaignDetails.templateName && (
                                        <div className={`${className}__detail-card`}>
                                            <div
                                                className={`${className}__detail-card-icon ${className}__detail-card-icon--purple`}
                                            >
                                                <MessageSquare size={17} />
                                            </div>

                                            <div>
                                                <span>Template</span>
                                                <strong>
                                                    {campaignDetails.templateName}
                                                </strong>
                                            </div>
                                        </div>
                                    )}

                                    <div className={`${className}__detail-card`} onClick={()=> setShowRecipients(true)}>
                                        <div
                                            className={`${className}__detail-card-icon ${className}__detail-card-icon--blue`}
                                        >
                                            <Users size={17} />
                                        </div>

                                        <div>
                                            <span>Recipients</span>
                                            <strong>
                                                {sendToCount} contacts
                                            </strong>
                                        </div>
                                    </div>

                                    <div className={`${className}__detail-card`}>
                                        <div
                                            className={`${className}__detail-card-icon ${className}__detail-card-icon--green`}
                                        >
                                            <Phone size={17} />
                                        </div>

                                        <div>
                                            <span>Phone Number</span>
                                            <strong>
                                                {getPhoneNumber()}
                                            </strong>
                                        </div>
                                    </div>
                                    <div
                                        className={`${className}__detail-card`}
                                    >
                                        <div
                                            className={`${className}__detail-card-icon ${className}__detail-card-icon--orange`}
                                        >
                                            <RefreshCw size={17} />
                                        </div>

                                        <div>
                                            <span>Retry Count</span>
                                            <strong>
                                                {!!campaignDetails?.retryCount ? campaignDetails?.retryCount : 3}
                                            </strong>
                                        </div>
                                    </div>
                                </div>

                                {/* Performance */}
                                <div className={`${className}__performance`}>
                                    <h4>Performance</h4>

                                    <div className={`${className}__performance-grid`}>
                                        <div>
                                            <div
                                                className={`${className}__performance-header`}
                                            >
                                                <span>Delivery rate</span>
                                                <strong className="is-green">
                                                    {deliveryRate}%
                                                </strong>
                                            </div>

                                            <div
                                                className={`${className}__progress`}
                                            >
                                                <span
                                                    className={`${className}__progress-fill ${className}__progress-fill--green`}
                                                    style={{
                                                        width: `${deliveryRate}%`
                                                    }}
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <div
                                                className={`${className}__performance-header`}
                                            >
                                                <span>Open rate</span>
                                                <strong className="is-purple">
                                                    {openRate}%
                                                </strong>
                                            </div>

                                            <div
                                                className={`${className}__progress`}
                                            >
                                                <span
                                                    className={`${className}__progress-fill ${className}__progress-fill--purple`}
                                                    style={{
                                                        width: `${openRate}%`
                                                    }}
                                                />
                                            </div>
                                        </div>

                                        {retry > 0 && (
                                            <div>
                                                <div
                                                    className={`${className}__performance-header`}
                                                >
                                                    <span>Retry rate</span>
                                                    <strong className="is-orange">
                                                        {total > 0
                                                            ? Math.round(
                                                                  (retry / total) *
                                                                      100
                                                              )
                                                            : 0}
                                                        %
                                                    </strong>
                                                </div>

                                                <div
                                                    className={`${className}__progress`}
                                                >
                                                    <span
                                                        className={`${className}__progress-fill ${className}__progress-fill--orange`}
                                                        style={{
                                                            width: `${
                                                                total > 0
                                                                    ? Math.round(
                                                                          (retry /
                                                                              total) *
                                                                              100
                                                                      )
                                                                    : 0
                                                            }%`
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* History */}
                                {campaignHistory.length > 0 && (
                                    <div className={`${className}__history`}>
                                        <h4>Activity</h4>

                                        <div className={`${className}__timeline`}>
                                            {campaignHistory.map((history, index) => (
                                                <div
                                                    className={`${className}__timeline-item`}
                                                    key={index}
                                                >
                                                    <div
                                                        className={`${className}__timeline-dot`}
                                                    />

                                                    <div
                                                        className={`${className}__timeline-content`}
                                                    >
                                                        <strong>
                                                            {history.property}
                                                            {history.description &&
                                                                ` — ${history.description}`}
                                                        </strong>

                                                        <span>
                                                            {history.changeByObj
                                                                ?.fullName ||
                                                                history.changeBy}{' '}
                                                            ·{' '}
                                                            {fmt(
                                                                history.timeStamp
                                                            )}
                                                        </span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                        {activeTab === 'retryDetails' && (
                            <div className={`${className}__retry-details`}>
                                <FailedMsgView
                                    campaign={campaign}
                                    campaignStatus={stats}
                                    handleSelectContact={handleSelectContact}
                                />
                            </div>
                        )}
                    </section>
                    <aside className={`${className}__sidebar`}>
                        <div className={`${className}__sidebar-card`}>
                            <div className={`${className}__sidebar-header`}>
                                <div>
                                    <span className={`${className}__eyebrow`}>
                                        MESSAGE
                                    </span>
                                    <h3>Template used</h3>
                                </div>

                                <div
                                    className={`${className}__sidebar-icon`}
                                >
                                    <MessageSquare size={16} />
                                </div>
                            </div>

                            <div className={`${className}__template-preview`}>
                                <WhatsappTemplatePreview
                                    id={campaignDetails.templateId}
                                    hideBackground
                                    showReadMore={false}
                                />
                            </div>

                            {campaignDetails.templateName && (
                                <div className={`${className}__template-name`}>
                                    <span>Template</span>
                                    <strong>
                                        {campaignDetails.templateName}
                                    </strong>
                                </div>
                            )}
                        </div>

                        {/* Summary */}
                        <div className={`${className}__sidebar-card`}>
                            <div className={`${className}__sidebar-header`}>
                                <div>
                                    <span className={`${className}__eyebrow`}>
                                        SUMMARY
                                    </span>
                                    <h3>Campaign overview</h3>
                                </div>

                                <div
                                    className={`${className}__sidebar-icon`}
                                >
                                    <Zap size={16} />
                                </div>
                            </div>

                            <div className={`${className}__summary-list`}>
                                <div>
                                    <span>Status</span>
                                    <strong>{runBadge.label}</strong>
                                </div>

                                <div>
                                    <span>Recipients</span>
                                    <strong>{sendToCount}</strong>
                                </div>

                                <div>
                                    <span>Delivered</span>
                                    <strong>
                                        {delivered + opened}
                                    </strong>
                                </div>

                                <div>
                                    <span>Read</span>
                                    <strong>{opened}</strong>
                                </div>

                                <div>
                                    <span>Failed</span>
                                    <strong>{failed}</strong>
                                </div>

                                {retry > 0 && (
                                    <div>
                                        <span>Retry queue</span>
                                        <strong>{retry}</strong>
                                    </div>
                                )}
                            </div>
                        </div>
                    </aside>
                </main>
            </div>
            <ExportDownloadForm
                open={showConfirmDownloadForm}
                closePopup={toggleConfirmDownloadForm}
                defaultOptions={DEFAULT_DOWNLOAD_OPTIONS}
                splitLabels={{
                    '': 'None',
                    campaignStatus: 'Status',
                    error: 'Error'
                }}
                onExport={handleExport}
            />

            {showContact && (
                <ContactDetailsPage
                    reloadData={() => renderData()}
                    nameCardId={selectedContact?.nameCardId}
                    onClose={handleCloseContactView}
                />
            )}

			{showRecipients && (
				<CampaignRecipientsView
					reloadData={() => renderData()}
					campaign={campaign}
					onClose={() => setShowRecipients(false)}
				/>
			)}
            <Calendar
                open={showCalender}
                onClose={handleCalenderMenu}
                anchorEl={anchorCalenderMenu}
                highlightedDates={highlightedDates}
            />
        </>
    );
}

export default WhatsappCampaignStatusPage;
